import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sellerAPI } from '@/services/api';
import { Search, MapPin, Shield, Building2, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import SellerRatingBadge from '@/components/SellerRatingBadge';

const getMediaUrl = (url) => (url ? (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`) : '');

export default function SellersDirectoryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [q, setQ] = useState(searchParams.get('q') || '');

	useEffect(() => {
		setLoading(true);
		const params = {};
		const queryStr = searchParams.get('q');
		if (queryStr) params.q = queryStr;
		sellerAPI.list(params)
			.then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
			.catch(() => setItems([]))
			.finally(() => setLoading(false));
	}, [searchParams]);

	const onSearch = (e) => {
		e.preventDefault();
		const sp = new URLSearchParams();
		if (q.trim()) sp.set('q', q.trim());
		setSearchParams(sp);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen" data-testid="sellers-directory-page">
			<div className="bg-white border-b border-[#E6E2D6]">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
					<div className="flex items-center gap-3 mb-2">
						<Users className="w-6 h-6 text-[#8B4513]" />
						<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('seller.directory_title')}
						</h1>
					</div>
					<p className="text-sm text-[#666055] mb-6">{t('seller.directory_subtitle')}</p>
					<form onSubmit={onSearch} className="flex gap-3 max-w-xl">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666055]" />
							<Input
								value={q}
								onChange={(e) => setQ(e.target.value)}
								placeholder={t('seller.search_placeholder')}
								className="pl-11 h-12 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
								data-testid="seller-search-input"
							/>
						</div>
						<button
							type="submit"
							className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12 px-6 text-sm font-medium"
							data-testid="seller-search-submit"
						>
							{t('common.search')}
						</button>
					</form>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<p className="text-sm text-[#666055] mb-4">{total} {t('seller.results')}</p>
				{loading ? (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
						{[...Array(6)].map((_, i) => (
							<div key={i} className="bg-white rounded-sm border border-[#E6E2D6] overflow-hidden">
								<div className="aspect-[16/9] img-skeleton" />
								<div className="p-5 space-y-3">
									<div className="h-5 w-3/4 img-skeleton rounded" />
									<div className="h-4 w-1/2 img-skeleton rounded" />
								</div>
							</div>
						))}
					</div>
				) : items.length === 0 ? (
					<div className="text-center py-16 text-sm text-[#666055]" data-testid="no-sellers">
						{t('seller.no_sellers')}
					</div>
				) : (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sellers-grid">
						{items.map((s) => (
							<Link
								key={s.id}
								to={`/u/${s.slug}`}
								className="bg-white border border-[#E6E2D6] rounded-sm overflow-hidden group hover:border-[#8B4513] transition-colors"
								data-testid={`seller-card-${s.slug}`}
							>
								<div className="aspect-[16/9] bg-gradient-to-br from-[#1A3C34] via-[#2A5048] to-[#8B4513] relative overflow-hidden">
									{s.cover_image ? (
										<img src={getMediaUrl(s.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
									) : (
										<div className="absolute inset-0 flex items-center justify-center">
											<Building2 className="w-12 h-12 text-white/40" />
										</div>
									)}
									{s.is_verified && (
										<span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#1A3C34] text-white
												px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm">
                      <Shield className="w-3 h-3" /> {t('seller.verified')}
                    </span>
									)}
								</div>
								<div className="p-5">
									<h3 className="text-lg font-semibold text-[#1A3C34] line-clamp-1 group-hover:text-[#8B4513]
											transition-colors font-['Playfair_Display']">
										{s.company_name || s.name}
									</h3>
									{s.location && (
										<div className="flex items-center gap-1 mt-1 text-xs text-[#666055]">
											<MapPin className="w-3 h-3" /> {s.location}
										</div>
									)}
									<div className="mt-2">
										<SellerRatingBadge avg={s.rating_avg || 0} count={s.rating_count || 0} size="sm" />
									</div>
									<div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-[#3a3a3a]">
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.horse_count} {t('nav.horses')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.product_count} {t('nav.products')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.transport_count} {t('nav.transports')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.school_count} {t('nav.schools')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.stable_count} {t('nav.stables')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.trainer_count} {t('nav.trainers')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.event_count} {t('nav.events')}
										</span>
										<span className="bg-[#F2F0E9] px-2 py-1 rounded-sm">
											{s.vet_count} {t('nav.vets')}
										</span>
									</div>
								</div>
							</Link>
						))}
					</div>
				)}
			</div>
		</div>
	);
}